import * as React from 'react';
import type { AppleUserData } from '@/types/apple';
import { APPLE_CLIENT_ID } from '@/utils/socialAuthConfig';

interface UseAppleLoginOptions {
  onSuccess: (idToken: string, userData?: AppleUserData) => Promise<void>;
  onError?: (error: unknown) => void;
}

/**
 * Returns a callback that triggers Apple Sign In via the AppleID JS SDK.
 * On success calls `onSuccess` with the identity token (and the optional first-time
 * user payload). Requires `VITE_APPLE_CLIENT_ID` and the AppleID script to be loaded.
 */
export function useAppleLogin({ onSuccess, onError }: UseAppleLoginOptions) {
  return React.useCallback(async () => {
    if (!APPLE_CLIENT_ID) {
      onError?.('Apple Sign In is not configured');
      return;
    }

    if (!window.AppleID) {
      onError?.('Apple Sign In is not available');
      return;
    }

    try {
      window.AppleID.auth.init({
        clientId: APPLE_CLIENT_ID,
        scope: 'name email',
        redirectURI: `${window.location.origin}/auth/apple/callback`,
        usePopup: true,
      });

      const response = await window.AppleID.auth.signIn();

      const idToken = response.authorization.id_token;
      const userData: AppleUserData | undefined = response.user
        ? {
            ...(response.user.name ? { name: response.user.name } : {}),
            ...(response.user.email !== undefined ? { email: response.user.email } : {}),
          }
        : undefined;

      await onSuccess(idToken, userData);
    } catch (error) {
      const err = error as Record<string, unknown>;
      if (err?.['error'] !== 'popup_closed_by_user') {
        onError?.(error);
      }
    }
  }, [onSuccess, onError]);
}
